import { useEffect, useMemo, useState } from "react";
import { Check, FileText, Loader2, Plus, Upload, X } from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { pageFitsDocumentType } from "@constructionautomation/backend/convex/lib/revisionDocumentTypes";

import { CountBadge } from "@/components/semantic-badges";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useRevisionSheets } from "@/features/sheet-index/use-revision-sheets";
import { toTitleCase } from "@/lib/formatting";
import { useAppNavigate } from "@/lib/use-app-navigate";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

/** Revision option displayed for a document; `_id` is written to revisionId/v1RevisionId/v2RevisionId, with optional classification metadata for labels. */
export interface PickerRevision {
  _id: string;
  revisionNumber: number;
  revisionName: string;
  effectiveDocumentType?: string;
  pageDocumentTypes?: string[];
  classificationStatus?: string;
}

/** Selectable project document and its available revisions; each revision is sorted and rendered within this document's card. */
export interface PickerDocument {
  _id: string;
  name: string;
  revisions: PickerRevision[];
}

/** One page of a revision, as sent to job creation (which builds a packet of the selected pages). */
export interface PickerPageSelection {
  revisionId: string;
  pageNumber: number;
}

/** Selection for single-revision sections; `revisionId` is the chosen revision for `documentId` and may be blank until configured. */
export interface SingleRevisionSelection {
  documentId: string;
  revisionId: string;
  /** Selected pages of `revisionId` in page order; absent means the whole document. */
  pages?: PickerPageSelection[];
}

/** Page numbers to send with a selection; undefined for whole-document selections. */
export function getSelectedPageNumbers(selection: SingleRevisionSelection | null | undefined) {
  return selection?.pages?.map((page) => page.pageNumber);
}

/** Page subsets of multi-section selections, in the shape job creation takes as `planPageNumbers`. */
export function getPlanPageNumbers(selections: SingleRevisionSelection[]) {
  return selections.flatMap((selection) => {
    const pageNumbers = getSelectedPageNumbers(selection);
    return pageNumbers
      ? [{ revisionId: selection.revisionId as Id<"projectDocumentRevisions">, pageNumbers }]
      : [];
  });
}

/** Message for a selection set to Select Pages with nothing checked, or undefined. */
export function getEmptyPageSelectionError(
  selections: Array<SingleRevisionSelection | null | undefined>
) {
  return selections.some((selection) => selection?.pages?.length === 0)
    ? "Select at least one page for each document set to Select Pages"
    : undefined;
}

/** Selection for pair-revision sections; `v1RevisionId` is the source revision and `v2RevisionId` is the newer target revision. */
export interface PairRevisionSelection {
  documentId: string;
  v1RevisionId: string;
  v2RevisionId: string;
}

/** Picker section configuration; selectionMode controls single vs multi cards, revisionMode controls one revision vs v1/v2 pairs, and optional allows empty sections. */
export interface PickerSection {
  key: string;
  label: string;
  documents: PickerDocument[];
  selectionMode: "single" | "multi";
  optional?: boolean;
  revisionMode: "single" | "pair";
  /** When true, this section's add button is disabled and its documents cannot be added. Already-selected documents remain editable. */
  disableAdd?: boolean;
  /** Overrides the add-button text; defaults to `Add {label}`. */
  addLabel?: string;
  /** Lets single-revision selections pick individual pages of this kind instead of the whole document. */
  pageKind?: "schedule" | "plan_set";
  allPagesLabel?: string;
  /** Optional override returning valid v2 choices for the selected document and v1 revision; defaults to newer revisions. */
  getAvailableV2Revisions?: (
    document: PickerDocument,
    v1RevisionId: string
  ) => PickerRevision[];
}

/** Single-document single-revision section value. */
export interface SingleSectionSelection {
  mode: "single";
  selection: SingleRevisionSelection | null;
}

/** Multi-document single-revision section value. */
export interface MultiSectionSelection {
  mode: "multi";
  selections: SingleRevisionSelection[];
}

/** Single-document pair-revision section value. */
export interface SinglePairSectionSelection {
  mode: "single-pair";
  selection: PairRevisionSelection | null;
}

/** Multi-document pair-revision section value. */
export interface MultiPairSectionSelection {
  mode: "multi-pair";
  selections: PairRevisionSelection[];
}

/** Controlled value for a section; the mode must match the section's selectionMode/revisionMode combination. */
export type SectionSelections =
  | SingleSectionSelection
  | MultiSectionSelection
  | SinglePairSectionSelection
  | MultiPairSectionSelection;

/** Props for the controlled picker; `isLoading` shows a spinner and `disabled` prevents document/revision changes. */
export interface ProjectDocumentPickerProps {
  sections: PickerSection[];
  values: Record<string, SectionSelections>;
  onChange: (sectionKey: string, value: SectionSelections) => void;
  isLoading: boolean;
  disabled?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function sortedRevisions(revisions: PickerRevision[]) {
  return revisions.slice().sort(
    (a, b) =>
      a.revisionNumber - b.revisionNumber || a._id.localeCompare(b._id)
  );
}

function revLabel(r: PickerRevision) {
  return `${r.revisionName} (r${r.revisionNumber})`;
}

function defaultV2Revisions(
  doc: PickerDocument,
  v1Id: string
): PickerRevision[] {
  const v1 = doc.revisions.find((r) => r._id === v1Id);
  if (!v1) return [];
  return doc.revisions
    .filter((r) => r.revisionNumber > v1.revisionNumber)
    .sort(
      (a, b) =>
        a.revisionNumber - b.revisionNumber || a._id.localeCompare(b._id)
    );
}

function getSelectedDocIds(value: SectionSelections): Set<string> {
  switch (value.mode) {
    case "single":
      return value.selection ? new Set([value.selection.documentId]) : new Set();
    case "multi":
      return new Set(value.selections.map((s) => s.documentId));
    case "single-pair":
      return value.selection ? new Set([value.selection.documentId]) : new Set();
    case "multi-pair":
      return new Set(value.selections.map((s) => s.documentId));
  }
}

// ---------------------------------------------------------------------------
// Selected document card — single revision
// ---------------------------------------------------------------------------

type PageMode = "document" | "pages";

const PAGE_MODE_OPTIONS = [
  { value: "document", label: "Whole Document" },
  { value: "pages", label: "Select Pages" },
] as const;

function pageCountLabel(count: number) {
  return `${count} ${count === 1 ? "page" : "pages"}`;
}

/**
 * Whole document or individual pages of one revision. Only pages of the
 * section's kind are offered; revisions without an indexed page of that kind
 * render nothing, so they can only be used whole.
 */
function PageSelectionPanel({
  revisionId,
  pageKind,
  allPagesLabel,
  pages,
  disabled,
  onPagesChange,
}: {
  revisionId: string;
  pageKind: NonNullable<PickerSection["pageKind"]>;
  allPagesLabel?: string;
  pages: PickerPageSelection[] | undefined;
  disabled: boolean;
  onPagesChange: (pages: PickerPageSelection[] | undefined) => void;
}) {
  const { data: sheets } = useRevisionSheets(revisionId as Id<"projectDocumentRevisions">);
  const [search, setSearch] = useState("");
  const eligibleSheets = useMemo(
    () => (sheets ?? []).filter((sheet) => pageFitsDocumentType(sheet.pageType, pageKind)),
    [sheets, pageKind]
  );
  const visibleSheets = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return eligibleSheets;
    return eligibleSheets.filter((sheet) =>
      [sheet.sheetNumber, sheet.sheetTitle, String(sheet.pageNumber)].some((value) =>
        value?.toLowerCase().includes(term)
      )
    );
  }, [eligibleSheets, search]);

  if (!sheets || eligibleSheets.length === 0) return null;

  const selected = new Set(pages?.map((page) => page.pageNumber));
  const emit = (pageNumbers: Set<number>) =>
    onPagesChange(
      [...pageNumbers]
        .sort((a, b) => a - b)
        .map((pageNumber) => ({ revisionId, pageNumber }))
    );
  const togglePage = (pageNumber: number, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(pageNumber);
    else next.delete(pageNumber);
    emit(next);
  };
  const visibleSelectedCount = visibleSheets.filter((sheet) => selected.has(sheet.pageNumber)).length;
  const selectAllState =
    visibleSheets.length > 0 && visibleSelectedCount === visibleSheets.length
      ? true
      : visibleSelectedCount > 0
        ? "indeterminate"
        : false;
  const toggleVisible = (checked: boolean) => {
    const next = new Set(selected);
    for (const sheet of visibleSheets) {
      if (checked) next.add(sheet.pageNumber);
      else next.delete(sheet.pageNumber);
    }
    emit(next);
  };

  return (
    <div className="space-y-2 border-t px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl<PageMode>
          aria-label="Pages to use"
          size="compact"
          value={pages ? "pages" : "document"}
          options={allPagesLabel ? [{ value: "document", label: allPagesLabel }, PAGE_MODE_OPTIONS[1]] : PAGE_MODE_OPTIONS}
          disabled={disabled}
          onValueChange={(mode) =>
            mode === "pages"
              ? emit(new Set(eligibleSheets.map((sheet) => sheet.pageNumber)))
              : onPagesChange(undefined)
          }
        />
        <span className="ms-auto text-xs text-muted-foreground tabular-nums">
          {pages
            ? `${selected.size} of ${pageCountLabel(sheets.length)} selected`
            : eligibleSheets.length < sheets.length
              ? `${eligibleSheets.length} of ${pageCountLabel(sheets.length)} match`
              : pageCountLabel(sheets.length)}
        </span>
      </div>
      {pages ? (
        <>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search sheet number or title..."
            aria-label="Search pages"
            className="h-8 text-sm"
            disabled={disabled}
          />
          <div className="max-h-64 overflow-y-auto rounded-md border">
            <label className="flex items-center gap-2 border-b bg-muted/70 px-2 py-1.5 text-xs font-semibold">
              <Checkbox
                checked={selectAllState}
                onCheckedChange={(checked) => toggleVisible(checked === true)}
                disabled={disabled || visibleSheets.length === 0}
                aria-label={search ? "Select all matching pages" : "Select all pages"}
              />
              {search ? "Select All Matching" : "Select All"}
            </label>
            {visibleSheets.length === 0 ? (
              <p className="px-2 py-3 text-center text-xs text-muted-foreground">
                No pages match &ldquo;{search}&rdquo;.
              </p>
            ) : (
              visibleSheets.map((sheet) => (
                <label
                  key={sheet.pageNumber}
                  className="flex cursor-pointer items-center gap-2 border-b px-2 py-1.5 text-sm last:border-b-0 hover:bg-muted/50"
                >
                  <Checkbox
                    checked={selected.has(sheet.pageNumber)}
                    onCheckedChange={(checked) => togglePage(sheet.pageNumber, checked === true)}
                    disabled={disabled}
                    aria-label={`Page ${sheet.pageNumber}`}
                  />
                  <span className="w-20 shrink-0 truncate font-medium tabular-nums">
                    {sheet.sheetNumber ?? "—"}
                  </span>
                  <span className="min-w-0 flex-1 truncate">
                    {sheet.sheetTitle ?? <span className="text-muted-foreground">Untitled</span>}
                  </span>
                  <Badge variant="outline" size="sm">
                    {toTitleCase(sheet.pageType)}
                  </Badge>
                  <span className="w-10 shrink-0 text-end text-xs text-muted-foreground tabular-nums">
                    Pg {sheet.pageNumber}
                  </span>
                </label>
              ))
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function SelectedSingleCard({
  doc,
  revisionId,
  pageKind,
  allPagesLabel,
  pages,
  disabled,
  onRevisionChange,
  onPagesChange,
  onRemove,
}: {
  doc: PickerDocument;
  revisionId: string;
  pageKind?: PickerSection["pageKind"];
  allPagesLabel?: string;
  pages?: PickerPageSelection[];
  disabled: boolean;
  onRevisionChange: (revisionId: string) => void;
  onPagesChange: (pages: PickerPageSelection[] | undefined) => void;
  onRemove: () => void;
}) {
  const revisions = sortedRevisions(doc.revisions);
  return (
    <div className="rounded-md border bg-background">
      <div className="flex items-center gap-2 px-3 py-2">
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate text-sm font-medium">{doc.name}</span>
        <Select
          value={revisionId}
          onValueChange={onRevisionChange}
          disabled={disabled}
        >
          <SelectTrigger className="ms-auto h-7 w-auto min-w-[120px] gap-1 text-xs">
            <SelectValue placeholder="Revision" />
          </SelectTrigger>
          <SelectContent>
            {revisions.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {revLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <IconButton
          label={`Remove ${doc.name}`}
          icon={X}
          size="xs"
          className="text-muted-foreground"
          iconClassName="size-3.5"
          disabled={disabled}
          onClick={onRemove}
        />
      </div>
      {pageKind && revisionId ? (
        <PageSelectionPanel
          key={revisionId}
          revisionId={revisionId}
          pageKind={pageKind}
          allPagesLabel={allPagesLabel}
          pages={pages}
          disabled={disabled}
          onPagesChange={onPagesChange}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Selected document card — pair revision (RevPilot)
// ---------------------------------------------------------------------------

function SelectedPairCard({
  doc,
  v1RevisionId,
  v2RevisionId,
  disabled,
  onV1Change,
  onV2Change,
  onRemove,
  getV2,
}: {
  doc: PickerDocument;
  v1RevisionId: string;
  v2RevisionId: string;
  disabled: boolean;
  onV1Change: (id: string) => void;
  onV2Change: (id: string) => void;
  onRemove: () => void;
  getV2: (doc: PickerDocument, v1Id: string) => PickerRevision[];
}) {
  const v1Revisions = sortedRevisions(doc.revisions);
  const v2Revisions = v1RevisionId ? getV2(doc, v1RevisionId) : [];

  return (
    <div className="space-y-2 rounded-md border bg-background px-3 py-2">
      <div className="flex items-center gap-2">
        <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="min-w-0 truncate text-sm font-medium">
          {doc.name}
        </span>
        <IconButton
          label={`Remove ${doc.name}`}
          icon={X}
          size="xs"
          className="ms-auto text-muted-foreground"
          iconClassName="size-3.5"
          disabled={disabled}
          onClick={onRemove}
        />
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        <Select
          value={v1RevisionId}
          onValueChange={onV1Change}
          disabled={disabled}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="v1 (older)" />
          </SelectTrigger>
          <SelectContent>
            {v1Revisions.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {revLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={v2RevisionId}
          onValueChange={onV2Change}
          disabled={disabled || !v1RevisionId}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue placeholder="v2 (newer)" />
          </SelectTrigger>
          <SelectContent>
            {v2Revisions.map((r) => (
              <SelectItem key={r._id} value={r._id}>
                {revLabel(r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add-document selector — one button + filtered combobox per document type
// ---------------------------------------------------------------------------

/** Minimal section shape the selector needs to render an add button and its document list. */
interface SelectorSection {
  key: string;
  label: string;
  documents: PickerDocument[];
  disableAdd?: boolean;
  addLabel?: string;
}

function AddSectionButton({
  section,
  selectedIds,
  disabled,
  onSelect,
}: {
  section: SelectorSection;
  selectedIds: Set<string>;
  disabled: boolean;
  onSelect: (sectionKey: string, doc: PickerDocument) => void;
}) {
  const [open, setOpen] = useState(false);

  const buttonDisabled = disabled || section.disableAdd === true;
  const label = section.addLabel ?? `Add ${section.label}`;

  useEffect(() => {
    if (buttonDisabled) {
      setOpen(false);
    }
  }, [buttonDisabled]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={buttonDisabled}
          className="gap-1.5"
        >
          <Plus className="size-3.5" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search documents..." />
          <CommandList>
            <CommandEmpty>No documents found.</CommandEmpty>
            <CommandGroup heading={section.label}>
              {section.documents.map((doc) => {
                const isSelected = selectedIds.has(doc._id);
                return (
                  <CommandItem
                    key={doc._id}
                    value={`${doc.name} ${doc._id}`}
                    onSelect={() => {
                      if (buttonDisabled || isSelected) return;
                      onSelect(section.key, doc);
                      setOpen(false);
                    }}
                    disabled={buttonDisabled || isSelected}
                  >
                    <FileText className="size-3.5" />
                    <span className="truncate">{doc.name}</span>
                    {isSelected ? (
                      <Check className="ms-auto size-3.5 text-primary" />
                    ) : (
                      <CountBadge
                        value={doc.revisions.length}
                        label={doc.revisions.length === 1 ? "revision" : "revisions"}
                        className="ms-auto"
                      />
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function AddDocumentSelector({
  sections,
  selectedIds,
  disabled,
  onSelect,
}: {
  sections: SelectorSection[];
  /** Documents each section's add button treats as already selected. */
  selectedIds: (sectionKey: string) => Set<string>;
  disabled: boolean;
  onSelect: (sectionKey: string, doc: PickerDocument) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {sections.map((section) => (
        <AddSectionButton
          key={section.key}
          section={section}
          selectedIds={selectedIds(section.key)}
          disabled={disabled}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section renderer
// ---------------------------------------------------------------------------

function PickerSectionView({
  section,
  value,
  onChange,
  disabled,
}: {
  section: PickerSection;
  value: SectionSelections;
  onChange: (value: SectionSelections) => void;
  disabled: boolean;
}) {
  const getV2 = section.getAvailableV2Revisions ?? defaultV2Revisions;

  // Single-select, single revision
  if (
    (value.mode === "single") &&
    section.revisionMode === "single"
  ) {
    const sel = value.selection;
    const doc = sel
      ? section.documents.find((d) => d._id === sel.documentId)
      : null;
    return doc && sel ? (
      <SelectedSingleCard
        doc={doc}
        revisionId={sel.revisionId}
        pageKind={section.pageKind}
        allPagesLabel={section.allPagesLabel}
        pages={sel.pages}
        disabled={disabled}
        onRevisionChange={(revisionId) =>
          onChange({
            mode: "single",
            selection: { documentId: sel.documentId, revisionId },
          })
        }
        onPagesChange={(pages) =>
          onChange({ mode: "single", selection: { ...sel, pages } })
        }
        onRemove={() => onChange({ mode: "single", selection: null })}
      />
    ) : null;
  }

  // Multi-select, single revision
  if (value.mode === "multi" && section.revisionMode === "single") {
    return value.selections.length > 0 ? (
      <div className="space-y-1.5">
        {value.selections.map((sel) => {
          const doc = section.documents.find((d) => d._id === sel.documentId);
          if (!doc) return null;
          return (
            <SelectedSingleCard
              key={sel.documentId}
              doc={doc}
              revisionId={sel.revisionId}
              pageKind={section.pageKind}
              allPagesLabel={section.allPagesLabel}
              pages={sel.pages}
              disabled={disabled}
              onRevisionChange={(revisionId) =>
                onChange({
                  mode: "multi",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { documentId: s.documentId, revisionId }
                      : s
                  ),
                })
              }
              onPagesChange={(pages) =>
                onChange({
                  mode: "multi",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId ? { ...s, pages } : s
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  mode: "multi",
                  selections: value.selections.filter(
                    (s) => s.documentId !== sel.documentId
                  ),
                })
              }
            />
          );
        })}
      </div>
    ) : null;
  }

  // Single-select, pair revision
  if (value.mode === "single-pair" && section.revisionMode === "pair") {
    const sel = value.selection;
    const doc = sel
      ? section.documents.find((d) => d._id === sel.documentId)
      : null;
    return doc && sel ? (
      <SelectedPairCard
        doc={doc}
        v1RevisionId={sel.v1RevisionId}
        v2RevisionId={sel.v2RevisionId}
        disabled={disabled}
        getV2={getV2}
        onV1Change={(v1RevisionId) =>
          onChange({
            mode: "single-pair",
            selection: { ...sel, v1RevisionId, v2RevisionId: "" },
          })
        }
        onV2Change={(v2RevisionId) =>
          onChange({
            mode: "single-pair",
            selection: { ...sel, v2RevisionId },
          })
        }
        onRemove={() => onChange({ mode: "single-pair", selection: null })}
      />
    ) : null;
  }

  // Multi-select, pair revision
  if (value.mode === "multi-pair") {
    return value.selections.length > 0 ? (
      <div className="space-y-1.5">
        {value.selections.map((sel) => {
          const doc = section.documents.find((d) => d._id === sel.documentId);
          if (!doc) return null;
          return (
            <SelectedPairCard
              key={sel.documentId}
              doc={doc}
              v1RevisionId={sel.v1RevisionId}
              v2RevisionId={sel.v2RevisionId}
              disabled={disabled}
              getV2={getV2}
              onV1Change={(v1RevisionId) =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { ...s, v1RevisionId, v2RevisionId: "" }
                      : s
                  ),
                })
              }
              onV2Change={(v2RevisionId) =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.map((s) =>
                    s.documentId === sel.documentId
                      ? { ...s, v2RevisionId }
                      : s
                  ),
                })
              }
              onRemove={() =>
                onChange({
                  mode: "multi-pair",
                  selections: value.selections.filter(
                    (s) => s.documentId !== sel.documentId
                  ),
                })
              }
            />
          );
        })}
      </div>
    ) : null;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/** Renders the controlled multi-section document picker and emits section-scoped selection updates through onChange. */
export function ProjectDocumentPicker({
  sections,
  values,
  onChange,
  isLoading,
  disabled = false,
}: ProjectDocumentPickerProps) {
  const navigate = useAppNavigate();

  // Collect all selected doc IDs across all sections
  const allSelectedIds = new Set<string>();
  for (const section of sections) {
    const val = values[section.key];
    if (val) {
      for (const id of getSelectedDocIds(val)) allSelectedIds.add(id);
    }
  }
  // A document picked by page can give different pages to different sections
  // (plan and schedule pages of one mixed set), so those only dedupe within themselves.
  const getAddSelectedIds = (sectionKey: string) => {
    const section = sections.find((candidate) => candidate.key === sectionKey);
    const value = values[sectionKey];
    if (!section?.pageKind) return allSelectedIds;
    return value ? getSelectedDocIds(value) : new Set<string>();
  };

  const handleComboboxSelect = (sectionKey: string, doc: PickerDocument) => {
    const section = sections.find((s) => s.key === sectionKey);
    if (!section) return;

    const autoRev =
      doc.revisions.length === 1 ? doc.revisions[0]!._id : "";
    const val = values[sectionKey];

    if (section.revisionMode === "pair") {
      if (section.selectionMode === "single") {
        onChange(sectionKey, {
          mode: "single-pair",
          selection: { documentId: doc._id, v1RevisionId: "", v2RevisionId: "" },
        });
        return;
      }

      const prev =
        val?.mode === "multi-pair" ? val.selections : [];
      onChange(sectionKey, {
        mode: "multi-pair",
        selections: [
          ...prev.filter((s) => s.documentId !== doc._id),
          { documentId: doc._id, v1RevisionId: "", v2RevisionId: "" },
        ],
      });
      return;
    }

    if (section.selectionMode === "single") {
      onChange(sectionKey, {
        mode: "single",
        selection: { documentId: doc._id, revisionId: autoRev },
      });
    } else {
      const prev = val?.mode === "multi" ? val.selections : [];
      if (prev.some((s) => s.documentId === doc._id)) return;
      onChange(sectionKey, {
        mode: "multi",
        selections: [
          ...prev,
          { documentId: doc._id, revisionId: autoRev },
        ],
      });
    }
  };

  const hasAnySelected = allSelectedIds.size > 0;

  return (
    <section className="space-y-3 rounded-lg border-2 bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold leading-none tracking-tight">
            Source Documents
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {sections.some((section) => section.pageKind)
              ? "Add documents from your project library, then select revisions or individual pages."
              : "Add documents from your project library and select revisions."}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center rounded-lg border py-8 text-sm text-muted-foreground">
          <Loader2 className="me-2 size-4 animate-spin" aria-hidden="true" />
          Loading project documents...
        </div>
      ) : (
        <>
          {/* Selected document cards grouped by section */}
          {sections.map((section) => {
            const val = values[section.key];
            if (!val) return null;
            const hasItems =
              (val.mode === "single" && val.selection !== null) ||
              (val.mode === "multi" && val.selections.length > 0) ||
              (val.mode === "single-pair" && val.selection !== null) ||
              (val.mode === "multi-pair" && val.selections.length > 0);
            if (!hasItems) return null;
            return (
              <div key={section.key}>
                {sections.length > 1 ? (
                  <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </p>
                ) : null}
                <PickerSectionView
                  section={section}
                  value={val}
                  onChange={(v) => onChange(section.key, v)}
                  disabled={disabled}
                />
              </div>
            );
          })}

          {!hasAnySelected ? (
            <div className="rounded-md border border-dashed bg-muted/30 px-4 py-5 text-center text-sm text-muted-foreground">
              No documents selected. Click &ldquo;Add document&rdquo; to get started.
            </div>
          ) : null}

          <AddDocumentSelector
            sections={sections.map((s) => ({
              key: s.key,
              label: s.label,
              documents: s.documents,
              disableAdd: s.disableAdd,
              addLabel: s.addLabel,
            }))}
            selectedIds={getAddSelectedIds}
            disabled={disabled}
            onSelect={handleComboboxSelect}
          />
        </>
      )}

      <div className="border-t pt-3">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          onClick={() =>
            navigate({ to: "/project/documents", title: "Project Documents" })
          }
        >
          <Upload className="size-4" />
          Upload to Project Documents
        </Button>
        <p className="mt-1 text-xs text-muted-foreground">
          Upload documents to your project library, then select them here.
        </p>
      </div>
    </section>
  );
}
